import React, { useState, useEffect, useCallback, useRef } from 'react';
import { agingColor } from './bankAging';

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface BankSummaryData {
  total_available_lbs: number;
  lot_count: number;
  oldest_age_days: number | null;
  buckets: { fresh: number; aging: number; stale: number };
}

interface Allocation {
  order_number: string;
  customer: string;
  delivery_date: string | null;   // ship date (who/when the running batch is for)
  lbs: number;
  destination: string; // 'bag' | 'pellet' | 'ss'
  package_type: string;
  package_size_lbs: number;
  package_count: number;
  package_display: string;
  is_supersack: boolean;
}

interface Batch {
  id: number;
  batch_number: string;
  formula_name: string;
  formula_code: string;
  feed_form: string;
  planned_weight: number;
  actual_weight: number | null;
  status: string;
  planned_date: string | null;
  started_at: string | null;
  completed_at: string | null;
  priority: number;
  notes: string;
  allocations: Allocation[];
  total_allocated?: number;
  feed_bank_excess?: number;
  stale_warning?: boolean;
  is_morning_mill?: boolean;
}

interface OrderLineItem {
  id: number;
  line_number: number;
  formula_name: string;
  formula_code: string;
  is_pelleted: boolean;
  form: string;
  package_type: string;
  is_supersack?: boolean;
  total_lbs: number;
  number_of_packages: number;
  bag_size: number;
  line_total: number;
  is_promo: boolean;
  production_status: string;
}

interface PalletItem {
  name: string;
  form: string;
  bags: number;
  lbs: number;
  is_promo: boolean;
  is_supersack?: boolean;
  position: string;
}

interface Pallet {
  items: PalletItem[];
  total_bags: number;
  total_lbs: number;
  is_supersack?: boolean;
}

interface CalendarOrder {
  id: number | string;
  order_number?: string;
  customer?: string;
  total_lbs: number;
  total_bags?: number;
  total_pallets?: number;
  // Two-stage progress (milled + packed) for the box fill
  ordered_lbs?: number;
  milled_lbs?: number;
  packed_lbs?: number;
  milled_pct?: number;
  packed_pct?: number;
  has_bags?: boolean;
  has_supersacks?: boolean;
  has_bulk?: boolean;
  shipping_method?: string;
  status: string;
  is_rush?: boolean;
  ship_date?: string;
  delivery_date?: string;
  items?: OrderLineItem[];
  pallets?: Pallet[];
  pallet_count?: number;
  // Milk run fields
  type?: string;
  route?: string;
  routeName?: string;
  name?: string;
  stopCount?: number;
  totalPallets?: number;
  totalLbs?: number;
  capacityLbs?: number;
  capacityPallets?: number;
  truckerName?: string;
  truckerPhone?: string;
  quotePrice?: number | null;
  stops?: MilkRunStopData[];
  notes?: string;
}

interface MilkRunStopData {
  loadOrder: number;
  customer: string;
  city: string;
  pallets: number;
  lbs: number;
  items: { feed: string; bags: number; form: string; production_status: string }[];
  producedItems: number;
  totalItems: number;
  deliveryNotes: string;
}

interface MilkRunSuggestionData {
  id: string;
  type: string;
  route: string;
  routeName: string;
  suggestedDate: string;
  totalPallets: number;
  totalLbs: number;
  orderCount: number;
  customerCount: number;
  reason: string;
  confidence: number;
}

interface IngredientPill {
  name: string;
  onHand: number;
  weeksLeft: number;
  burnRate: number;
  reorderBy: string | null;
  leadTime: string;
  status: string; // 'order' | 'ordered' | 'ok'
  supplier: string | null;
  onOrder: number | null;
}

interface LauraAlert {
  id: number;
  title: string;
  summary: string;
  category: string;
  priority: string;
  action_link: string;
  created_at: string;
  potential_savings: number | null;
}

interface IngredientOrderItem {
  id: number;
  raw_material_id: number;
  raw_material_name: string;
  supplier: string | null;
  supplier_id: number | null;
  current_on_hand_lbs: number;
  projected_need_lbs: number;
  recommended_order_lbs: number;
  final_order_lbs: number;
  price_per_lb: number;
  estimated_cost: number;
  lead_time_weeks: number;
  estimated_delivery: string | null;
  urgency: string;
  laura_note: string;
}

interface IngredientOrderData {
  id: number;
  generated_at: string;
  coverage_weeks: number;
  status: string;
  total_cost: number;
  notes: string;
  emailed_at: string | null;
  emailed_to: string;
  created_by: string;
  items: IngredientOrderItem[];
}

// ═══════════════════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════════════════

const C = {
  red: '#E73C3E',
  teal: '#508590',
  brown: '#3F2021',
  tan: '#C3AD8B',
  sage: '#6C8062',
  cream: '#FAF9F6',
  white: '#FFFFFF',
  muted: '#8a8a8a',
  textDark: '#3F2021',
  border: '#e8e4de',
};

const STATION_COLORS: Record<string, { color: string; bg: string; label: string }> = {
  IN_PRODUCTION: { color: C.teal, bg: '#eef6f7', label: 'MIXING' },
  PACKAGING: { color: C.sage, bg: '#edf6f0', label: 'BAGGING' },
  PELLETING: { color: C.red, bg: '#fdf0f0', label: 'PELLETING' },
  PREP_BIN: { color: '#8a7040', bg: '#f8f4ec', label: 'PREP BIN' },
  PLANNED: { color: C.muted, bg: '#f5f5f5', label: 'PLANNED' },
  QUEUED: { color: C.muted, bg: '#f5f5f5', label: 'QUEUED' },
  COMPLETED: { color: C.sage, bg: '#edf6f0', label: 'COMPLETE' },
};

const PROD_STATUS: Record<string, { color: string; bg: string; label: string }> = {
  PRODUCED:  { color: C.sage,  bg: '#edf6f0', label: 'PRODUCED' },
  MIXING:    { color: C.teal,  bg: '#eef6f7', label: 'MIXING' },
  BAGGING:   { color: C.sage,  bg: '#edf6f0', label: 'BAGGING' },
  PELLETING: { color: C.red,   bg: '#fdf0f0', label: 'PELLETING' },
  QUEUED:    { color: '#8a7040', bg: '#f8f4ec', label: 'QUEUED' },
  PLANNED:   { color: C.muted, bg: '#f5f5f5', label: 'PLANNED' },
};

const PALLET_BAR_COLORS = [C.sage, C.tan, C.teal, '#8a7040', C.red];

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ═══════════════════════════════════════════════════════════════════════════
// API
// ═══════════════════════════════════════════════════════════════════════════

let _redirectingToLogin = false;

async function apiFetch(path: string, options?: RequestInit) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) },
    ...options,
  });
  if (res.status === 401 || res.status === 403) {
    // Session expired — try to refresh via cookie-based session
    // If already redirecting, don't pile on
    if (!_redirectingToLogin) {
      _redirectingToLogin = true;
      // Attempt a session refresh call (Django session auth)
      try {
        const refreshRes = await fetch(`${API_BASE}/auth/refresh/`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (refreshRes.ok) {
          // Retry the original request
          _redirectingToLogin = false;
          const retry = await fetch(`${API_BASE}${path}`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) },
            ...options,
          });
          if (retry.ok) return retry.json();
        }
      } catch { /* refresh failed */ }
      // Redirect to login
      window.location.href = '/login?next=/mill/command&reason=session_expired';
    }
    throw new Error('Session expired');
  }
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
}

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

const fmtLbs = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : `${n}`;
const fmtNum = (n: number) => n.toLocaleString();
const DAY_NAMES = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Compact ship-date for the NOW RUNNING order context, e.g. "Jun 23".
function fmtShipDate(iso: string | null): string {
  if (!iso) return 'TBD';
  const d = parseDate(iso);
  return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

function isToday(iso: string): boolean {
  const d = parseDate(iso);
  const t = new Date();
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
}

function getAlertIcon(category: string): string {
  const map: Record<string, string> = {
    production: '🏭', delivery: '🚚', inventory: '📦', customer: '👤',
    financial: '💰', operational: '⚙️', alert: '⚠️', milk_run: '🚛',
  };
  return map[category] || '💡';
}

// ═══════════════════════════════════════════════════════════════════════════
// Keyframe injection (once)
// ═══════════════════════════════════════════════════════════════════════════

const STYLE_ID = 'mill-command-keyframes';
function ensureKeyframes() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes mcPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
    @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700;800&display=swap');
  `;
  document.head.appendChild(style);
}

// ═══════════════════════════════════════════════════════════════════════════
// Floating recipe window (draggable + resizable, self-contained, no deps)
// ═══════════════════════════════════════════════════════════════════════════

interface RecipeIng { name: string; lbs: number; scale: 'BULK' | 'MICRO'; }

// Open-big defaults the floor settled on (read across the room). Operator-adjustable
// after open; tweak these to retune the starting view.
const DEFAULT_FONT_SCALE = 2.2;
const DEFAULT_WIN_SIZE = { w: 1050, h: 900 };

interface RecipeWindowPrefs { fontScale: number; w: number; h: number; x: number; y: number; }

const RecipeWindow: React.FC<{
  batchNumber: string;
  formulaName: string;
  ingredients: RecipeIng[] | null;   // null = still loading
  loading: boolean;
  onClose: () => void;
  nextLabel: { formulaName: string; batchNumber: string } | null;   // running batch differs from shown
  onLoadNext: () => void;
  savedPrefs: RecipeWindowPrefs | null;      // restored geometry/zoom (or null → defaults)
  onPersist: (p: RecipeWindowPrefs) => void; // debounced save on drag/resize/zoom end
}> = ({ batchNumber, formulaName, ingredients, loading, onClose, nextLabel, onLoadNext, savedPrefs, onPersist }) => {
  // Position, size, AND font zoom live INSIDE this component — initialized once,
  // mutated only by drag/resize/zoom. The dashboard's 15s poll re-renders the parent
  // but never remounts this window, so all three survive the poll.
  // Lazy initializers (run once on open): restore saved prefs if present, else the
  // BIG defaults — always clamped fully on-screen (a saved pos from another monitor
  // can't open off-screen).
  const [size, setSize] = useState(() => {
    const maxW = Math.round(window.innerWidth * 0.95);
    const maxH = Math.round(window.innerHeight * 0.95);
    return {
      w: Math.min(savedPrefs?.w ?? DEFAULT_WIN_SIZE.w, maxW),
      h: Math.min(savedPrefs?.h ?? DEFAULT_WIN_SIZE.h, maxH),
    };
  });
  const [pos, setPos] = useState(() => {
    const maxW = Math.round(window.innerWidth * 0.95);
    const maxH = Math.round(window.innerHeight * 0.95);
    const w = Math.min(savedPrefs?.w ?? DEFAULT_WIN_SIZE.w, maxW);
    const h = Math.min(savedPrefs?.h ?? DEFAULT_WIN_SIZE.h, maxH);
    if (savedPrefs) {
      return {
        x: Math.max(0, Math.min(savedPrefs.x, window.innerWidth - w)),   // fully on-screen
        y: Math.max(0, Math.min(savedPrefs.y, window.innerHeight - h)),
      };
    }
    return {
      x: Math.max(20, window.innerWidth - w - 30),                       // top-right-ish
      y: Math.min(70, Math.max(20, window.innerHeight - h - 20)),
    };
  });
  const [fontScale, setFontScale] = useState(() =>
    Math.max(0.8, Math.min(3.0, savedPrefs?.fontScale ?? DEFAULT_FONT_SCALE)));
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  // mode: 'both' (corner), 'x' (width only), 'y' (height only)
  const resize = useRef<{ x: number; y: number; w: number; h: number; mode: 'both' | 'x' | 'y' } | null>(null);

  useEffect(() => {
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
    const onMove = (e: MouseEvent) => {
      if (drag.current) {
        setPos({ x: e.clientX - drag.current.dx, y: e.clientY - drag.current.dy });
      } else if (resize.current) {
        const r = resize.current;
        const maxW = window.innerWidth * 0.95;
        const maxH = window.innerHeight * 0.95;
        let w = r.w, h = r.h;
        if (r.mode !== 'y') w = clamp(r.w + (e.clientX - r.x), 320, maxW);   // width axis
        if (r.mode !== 'x') h = clamp(r.h + (e.clientY - r.y), 240, maxH);   // height axis
        setSize({ w, h });
      }
    };
    const onUp = () => { drag.current = null; resize.current = null; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Debounced persist: save-on-END (not per-mousemove). While dragging/resizing, pos/size
  // change rapidly and reset the 500ms timer, so one gesture = one PUT ~500ms after it
  // stops; zoom (discrete) saves the same way. Skip the first render (initial defaults).
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstPersist = useRef(true);
  useEffect(() => {
    if (firstPersist.current) { firstPersist.current = false; return; }
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      onPersist({ fontScale, w: size.w, h: size.h, x: pos.x, y: pos.y });
    }, 500);
    return () => { if (persistTimer.current) clearTimeout(persistTimer.current); };
  }, [fontScale, size, pos, onPersist]);

  const startDrag = (e: React.MouseEvent) => {
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
  };
  const startResize = (mode: 'both' | 'x' | 'y') => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    resize.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h, mode };
  };
  const bumpFont = (delta: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setFontScale(s => Math.max(0.8, Math.min(3.0, Math.round((s + delta) * 100) / 100)));
  };

  const bulk = (ingredients || []).filter(i => i.scale === 'BULK');
  const micro = (ingredients || []).filter(i => i.scale !== 'BULK');

  // Font sizes scale with the zoom; section label scales lighter than the values.
  const nameSize = 22 * fontScale;
  const lbsSize = 30 * fontScale;
  const unitSize = 16 * fontScale;
  const labelSize = 15 * (1 + (fontScale - 1) * 0.5);
  // Row whitespace is decoupled from the font multiplier — gentle growth so big
  // font doesn't balloon the gaps. ~3px @100% → ~4px @220%. More rows fit on screen.
  const rowPadY = Math.round(3 + (fontScale - 1) * 0.8);

  const Group: React.FC<{ label: string; color: string; rows: RecipeIng[] }> = ({ label, color, rows }) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: labelSize, fontWeight: 800, letterSpacing: 1.5, color,
        borderBottom: `2px solid ${color}`, paddingBottom: 2, marginBottom: 2 }}>
        {label} SCALE
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 16, color: C.muted, fontStyle: 'italic' }}>—</div>
      ) : rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', gap: 16, padding: `${rowPadY}px 0`, borderBottom: `1px solid ${C.border}` }}>
          {/* Name fills the width; wraps rather than clips at extreme zoom. */}
          <span style={{ fontSize: nameSize, fontWeight: 600, color: C.textDark,
            flex: 1, minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.05 }}>{r.name}</span>
          {/* Poundage stays right-aligned, dominant, never wraps/clips. */}
          <span style={{ fontSize: lbsSize, fontWeight: 800, color: C.brown, lineHeight: 1.05,
            fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flexShrink: 0, textAlign: 'right' }}>
            {fmtNum(Math.round(r.lbs))}<span style={{ fontSize: unitSize, fontWeight: 600, color: C.muted }}> lbs</span>
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', left: pos.x, top: pos.y, width: size.w, height: size.h,
      background: C.white, borderRadius: 12, boxShadow: '0 12px 48px rgba(0,0,0,0.45)',
      border: `1px solid ${C.tan}55`, display: 'flex', flexDirection: 'column',
      zIndex: 2000, overflow: 'hidden',
    }}>
      {/* Drag header */}
      <div
        onMouseDown={startDrag}
        style={{ flexShrink: 0, cursor: 'grab', userSelect: 'none', background: C.brown,
          color: C.cream, padding: '10px 14px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 10 }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formulaName}</div>
          <div style={{ fontSize: 12, color: C.tan }}>{batchNumber} · mixing recipe</div>
        </div>
        {/* Zoom controls + close — stopPropagation so clicking them doesn't start a drag */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
             onMouseDown={(e) => e.stopPropagation()}>
          <button onClick={bumpFont(-0.15)} title="Smaller font"
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: C.cream,
              fontSize: 16, fontWeight: 800, lineHeight: 1, cursor: 'pointer', borderRadius: 5, padding: '4px 9px' }}>
            A−
          </button>
          <span style={{ fontSize: 12, color: C.tan, minWidth: 38, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(fontScale * 100)}%
          </span>
          <button onClick={bumpFont(0.15)} title="Bigger font"
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: C.cream,
              fontSize: 18, fontWeight: 800, lineHeight: 1, cursor: 'pointer', borderRadius: 5, padding: '4px 9px' }}>
            A+
          </button>
          <button onClick={onClose} title="Close"
            style={{ background: 'none', border: 'none', color: C.cream, fontSize: 24,
              lineHeight: 1, cursor: 'pointer', marginLeft: 4 }}>×</button>
        </div>
      </div>

      {/* Next-batch prompt — NEVER auto-swaps; the operator taps to load. Shown only
          when the running batch differs from the one on screen (poll re-evaluates it). */}
      {nextLabel && (
        <button
          onClick={onLoadNext}
          style={{ flexShrink: 0, width: '100%', textAlign: 'left', cursor: 'pointer',
            background: '#fef3c7', color: '#92400e', border: 'none',
            borderBottom: '1px solid #fcd34d', padding: '10px 16px',
            fontSize: 16, fontWeight: 700 }}
          title="Load this batch's recipe"
        >
          ▶ Next: {nextLabel.formulaName} ({nextLabel.batchNumber}) — tap to load
        </button>
      )}

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {loading || ingredients === null ? (
          <div style={{ fontSize: 18, color: C.muted, textAlign: 'center', marginTop: 30 }}>Loading recipe…</div>
        ) : ingredients.length === 0 ? (
          <div style={{ fontSize: 18, color: C.muted, textAlign: 'center', marginTop: 30 }}>No recipe ingredients.</div>
        ) : (
          <>
            <Group label="BULK" color={C.sage} rows={bulk} />
            <Group label="MICRO" color={C.teal} rows={micro} />
          </>
        )}
      </div>

      {/* Resize handles: right edge (width), bottom edge (height), corner (both) */}
      <div onMouseDown={startResize('x')} title="Drag to widen"
        style={{ position: 'absolute', top: 44, bottom: 18, right: 0, width: 10, cursor: 'ew-resize' }} />
      <div onMouseDown={startResize('y')} title="Drag to change height"
        style={{ position: 'absolute', left: 0, right: 18, bottom: 0, height: 10, cursor: 'ns-resize' }} />
      <div onMouseDown={startResize('both')} title="Drag to resize"
        style={{ position: 'absolute', right: 0, bottom: 0, width: 20, height: 20,
          cursor: 'nwse-resize', background: `linear-gradient(135deg, transparent 50%, ${C.tan} 50%)` }} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════

const MillCommandDashboard: React.FC = () => {
  // Data
  const [activeBatches, setActiveBatches] = useState<Batch[]>([]);
  const [productionPlan, setProductionPlan] = useState<Record<string, Batch[]>>({});
  const [calendar, setCalendar] = useState<Record<string, CalendarOrder[]>>({});
  const [ingredients, setIngredients] = useState<IngredientPill[]>([]);
  const [allClear, setAllClear] = useState(false);
  // Floating recipe pop-up — state is independent of activeBatches (which the 15s
  // poll replaces), so opening/dragging the window survives the poll.
  const [recipeBatch, setRecipeBatch] = useState<{ id: number; batch_number: string; formula_name: string } | null>(null);
  const [recipeIngredients, setRecipeIngredients] = useState<RecipeIng[] | null>(null);
  const [recipeLoading, setRecipeLoading] = useState(false);
  // Restored recipe-window geometry/zoom (per shared-kiosk user), loaded once on mount.
  const [recipePrefs, setRecipePrefs] = useState<RecipeWindowPrefs | null>(null);
  const [lauraAlerts, setLauraAlerts] = useState<LauraAlert[]>([]);
  const [ingredientOrder, setIngredientOrder] = useState<IngredientOrderData | null>(null);
  const [bankSummary, setBankSummary] = useState<BankSummaryData | null>(null);
  const [loading, setLoading] = useState(true);

  const [milkRunSuggestions, setMilkRunSuggestions] = useState<MilkRunSuggestionData[]>([]);

  // UI
  const [now, setNow] = useState(new Date());
  const [alertIdx, setAlertIdx] = useState(0);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<CalendarOrder | null>(null);
  const [showMilkRunModal, setShowMilkRunModal] = useState(false);
  const [selectedMilkRun, setSelectedMilkRun] = useState<CalendarOrder | null>(null);
  const [showIngModal, setShowIngModal] = useState(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editLbs, setEditLbs] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Drag-and-drop
  const [dragBatchId, setDragBatchId] = useState<number | null>(null);
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const alertTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // --- Resizable sections: [nowRunning%, prodPlan%, calendar%] ---
  const STORAGE_KEY = 'mill-cmd-sections';
  const defaultPcts = [15, 25, 60];
  const [sectionPcts, setSectionPcts] = useState<number[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) { const p = JSON.parse(saved); if (Array.isArray(p) && p.length === 3) return p; }
    } catch {}
    return defaultPcts;
  });
  const draggingRef = useRef<number | null>(null); // 0 = handle between NR & PP, 1 = handle between PP & CAL
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleDragStart = useCallback((handleIdx: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = handleIdx;
    const onMove = (me: MouseEvent) => {
      if (draggingRef.current === null || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const yPct = ((me.clientY - rect.top) / rect.height) * 100;
      setSectionPcts(prev => {
        const next = [...prev];
        const idx = draggingRef.current!;
        if (idx === 0) {
          // Dragging between NR and PP
          const newNR = Math.max(5, Math.min(40, yPct));
          const oldSum01 = prev[0] + prev[1];
          next[0] = newNR;
          next[1] = Math.max(10, oldSum01 - newNR);
          next[2] = 100 - next[0] - next[1];
          if (next[2] < 20) { next[2] = 20; next[1] = 100 - next[0] - next[2]; }
        } else {
          // Dragging between PP and CAL
          const newPPEnd = Math.max(prev[0] + 10, Math.min(80, yPct));
          next[1] = newPPEnd - prev[0];
          next[2] = 100 - prev[0] - next[1];
          if (next[2] < 20) { next[2] = 20; next[1] = 100 - prev[0] - next[2]; }
        }
        return next;
      });
    };
    const onUp = () => {
      draggingRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setSectionPcts(prev => { localStorage.setItem(STORAGE_KEY, JSON.stringify(prev)); return prev; });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // --- inject keyframes + font on mount ---
  useEffect(() => { ensureKeyframes(); }, []);

  // --- clock tick every second ---
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // --- Laura alert auto-rotate ---
  useEffect(() => {
    if (lauraAlerts.length <= 1) return;
    alertTimerRef.current = setInterval(() => {
      setAlertIdx(prev => (prev + 1) % lauraAlerts.length);
    }, 6000);
    return () => { if (alertTimerRef.current) clearInterval(alertTimerRef.current); };
  }, [lauraAlerts.length]);

  // --- Data fetching ---
  const fetchFast = useCallback(async () => {
    try {
      const b = await apiFetch('/mill/command/active-batches/');
      setActiveBatches(b);
    } catch (e) { console.error(e); }
  }, []);

  const fetchMedium = useCallback(async () => {
    // Fetch independently so one failure doesn't kill both
    try {
      const plan = await apiFetch('/mill/command/production-plan/');
      setProductionPlan(plan);
    } catch (e) { console.error('Production plan fetch failed:', e); }
    try {
      const cal = await apiFetch('/mill/command/calendar/');
      // Calendar response: { calendar: {...}, milk_run_suggestions: [...] }
      if (cal && cal.calendar) {
        setCalendar(cal.calendar);
        setMilkRunSuggestions(cal.milk_run_suggestions || []);
      } else {
        // Backwards compat: old format was just the date dict
        setCalendar(cal);
      }
    } catch (e) { console.error('Calendar fetch failed:', e); }
    try {
      const bank = await apiFetch('/production/feed-bank/availability_summary/');
      setBankSummary(bank);
    } catch (e) { console.error('Bank summary fetch failed:', e); }
  }, []);

  const fetchSlow = useCallback(async () => {
    try {
      const [ingRes, alerts, ingOrder] = await Promise.all([
        apiFetch('/mill/command/ingredients/'),
        apiFetch('/mill/command/laura-alerts/'),
        apiFetch('/mill/command/ingredient-order/'),
      ]);
      setIngredients(ingRes.ingredients || []);
      setAllClear(ingRes.allClear || false);
      // Inject milk run suggestions as Laura alerts
      const mrAlerts: LauraAlert[] = milkRunSuggestions.map(s => ({
        id: -Math.abs(parseInt(s.id.replace('MRS-', '')) || 0),
        title: `Milk Run Opportunity: ${s.routeName}`,
        summary: `${s.orderCount} orders building on ${s.routeName} — ${fmtNum(Math.round(s.totalLbs))} lbs. Enough for a milk run?`,
        category: 'milk_run',
        priority: 'high',
        action_link: '',
        created_at: s.suggestedDate,
        potential_savings: null,
      }));
      setLauraAlerts([...mrAlerts, ...(alerts || [])]);
      setIngredientOrder(ingOrder.order);
    } catch (e) { console.error(e); }
  }, []);

  // Open the floating recipe window for a running batch — fetches the SAME mixing
  // endpoint the Mixing table uses; keeps name + poundage + scale only.
  const openRecipe = useCallback(async (batch: Batch) => {
    setRecipeBatch({ id: batch.id, batch_number: batch.batch_number, formula_name: batch.formula_name });
    setRecipeIngredients(null);
    setRecipeLoading(true);
    try {
      const data = await apiFetch(`/production/mixing/${batch.id}/`);
      const ings: RecipeIng[] = (data.ingredients || []).map((i: any) => ({
        name: i.raw_material_name,
        lbs: i.planned_quantity,
        scale: i.scale === 'BULK' ? 'BULK' : 'MICRO',
      }));
      setRecipeIngredients(ings);
    } catch (e) {
      console.error('Recipe fetch failed:', e);
      setRecipeIngredients([]);
    } finally {
      setRecipeLoading(false);
    }
  }, []);

  // Load saved recipe-window prefs once on mount (before the window is ever opened).
  useEffect(() => {
    apiFetch('/mill/command/ui-prefs/')
      .then(d => { if (d && d.recipeWindow) setRecipePrefs(d.recipeWindow); })
      .catch(e => console.error('ui-prefs load failed:', e));
  }, []);

  // Fire-and-forget debounced save of the recipe-window geometry/zoom.
  const saveRecipePrefs = useCallback((rw: RecipeWindowPrefs) => {
    apiFetch('/mill/command/ui-prefs/', {
      method: 'PUT', body: JSON.stringify({ recipeWindow: rw }),
    }).catch(e => console.error('ui-prefs save failed:', e));
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchFast(), fetchMedium(), fetchSlow()]);
      setLoading(false);
    })();
    const t1 = setInterval(fetchFast, 15000);
    const t2 = setInterval(fetchMedium, 60000);
    const t3 = setInterval(fetchSlow, 300000);
    return () => { clearInterval(t1); clearInterval(t2); clearInterval(t3); };
  }, [fetchFast, fetchMedium, fetchSlow]);

  // --- Ingredient order handlers ---
  const handleEditItem = async (itemId: number) => {
    try {
      const body: Record<string, unknown> = {};
      if (editLbs) body.final_order_lbs = parseFloat(editLbs);
      const res = await apiFetch(`/mill/command/ingredient-order/items/${itemId}/`, {
        method: 'PATCH', body: JSON.stringify(body),
      });
      setIngredientOrder(res.order);
      setEditingItemId(null);
    } catch (e) { console.error(e); }
  };

  const handleEmailOrder = async () => {
    if (!emailTo) return;
    setEmailSending(true);
    try {
      await apiFetch('/mill/command/ingredient-order/email/', {
        method: 'POST', body: JSON.stringify({ email: emailTo }),
      });
      setEmailSent(true);
      fetchSlow();
    } catch (e) { console.error(e); }
    finally { setEmailSending(false); }
  };

  // --- Drag-and-drop reschedule ---
  const handleBatchDrop = async (batchId: number, newDate: string) => {
    // Find current date of batch for rollback
    let oldDate = '';
    let movedBatch: Batch | null = null;
    for (const [d, batches] of Object.entries(productionPlan)) {
      const found = batches.find(b => b.id === batchId);
      if (found) { oldDate = d; movedBatch = found; break; }
    }
    if (!movedBatch || oldDate === newDate) return;

    // Optimistic update
    setProductionPlan(prev => {
      const next = { ...prev };
      // Remove from old date
      next[oldDate] = (next[oldDate] || []).filter(b => b.id !== batchId);
      if (next[oldDate].length === 0) delete next[oldDate];
      // Add to new date
      next[newDate] = [...(next[newDate] || []), { ...movedBatch!, planned_date: newDate }];
      return next;
    });

    const dayDate = parseDate(newDate);
    const shortDay = SHORT_DAYS[dayDate.getDay()];
    const mo = dayDate.getMonth() + 1;
    const da = dayDate.getDate();
    setToast(`${movedBatch.batch_number} → ${shortDay} ${mo}/${da}`);
    setTimeout(() => setToast(null), 3000);

    try {
      await apiFetch(`/mill/command/batch/${batchId}/reschedule/`, {
        method: 'PATCH',
        body: JSON.stringify({ planned_date: newDate }),
      });
    } catch (e) {
      console.error('Reschedule failed:', e);
      // Revert on failure
      fetchMedium();
    }
  };

  // --- Computed values ---
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const planDates = Object.keys(productionPlan).sort();
  const totalPlanBatches = planDates.reduce((s, d) => s + productionPlan[d].length, 0);

  // Calendar: build 8 business-day columns (2 weeks of Tue-Wed-Thu-Fri)
  const calendarColumns = React.useMemo(() => {
    const cols: { date: Date; iso: string; orders: CalendarOrder[] }[] = [];
    const start = new Date(now);
    // Find next Tuesday (or today if Tuesday)
    const dow = start.getDay();
    const daysToTue = dow <= 2 ? (2 - dow) : (9 - dow);
    start.setDate(start.getDate() + (dow >= 2 && dow <= 5 ? 0 : daysToTue));
    // Generate 2 weeks of Tue-Fri
    let d = new Date(start);
    for (let week = 0; week < 2; week++) {
      // Find Tuesday of this week
      const weekStart = new Date(d);
      const wdow = weekStart.getDay();
      if (wdow > 5 || wdow < 2) {
        weekStart.setDate(weekStart.getDate() + (wdow <= 1 ? (2 - wdow) : (9 - wdow)));
      } else if (wdow > 2) {
        // Back to Tuesday
        weekStart.setDate(weekStart.getDate() - (wdow - 2));
      }
      for (let dayOff = 0; dayOff < 4; dayOff++) {
        const col = new Date(weekStart);
        col.setDate(weekStart.getDate() + dayOff);
        const iso = `${col.getFullYear()}-${String(col.getMonth() + 1).padStart(2, '0')}-${String(col.getDate()).padStart(2, '0')}`;
        cols.push({ date: col, iso, orders: calendar[iso] || [] });
      }
      d = new Date(weekStart);
      d.setDate(d.getDate() + 7);
    }
    return cols;
  }, [calendar, now]);

  // Active batch stats — use real package counts from allocations
  const totalBagsToday = activeBatches.reduce((s, b) =>
    s + b.allocations.filter(a => a.destination === 'bag').reduce((ss, a) => ss + (a.package_count || Math.round(a.lbs / (a.package_size_lbs || 40))), 0), 0);

  // ═════════════════════════════════════════════════════════════════════════
  // Render
  // ═════════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div style={{
        width: '100vw', height: '100vh', background: C.cream, display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontFamily: "'Open Sans', sans-serif",
        fontSize: 20, color: C.muted,
      }}>Loading Mill Command...</div>
    );
  }

  const pulse = { animation: 'mcPulse 2s ease-in-out infinite' };
  const pulseFast = { animation: 'mcPulse 1.5s ease-in-out infinite' };

  return (
    <div style={{
      width: '100vw', height: '100vh', overflow: 'hidden', background: C.cream,
      fontFamily: "'Open Sans', sans-serif", display: 'flex', flexDirection: 'column',
      gap: 8, padding: 8, boxSizing: 'border-box',
    }}>

      {/* ═══ 1. HEADER BAR ═══ */}
      <div style={{
        flexShrink: 0, background: C.brown, borderRadius: 10, padding: '10px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: C.cream }}>🌾 RED BRIDGE</span>
          <span style={{ color: C.tan, opacity: 0.4, fontSize: 18 }}>|</span>
          <span style={{ fontSize: 14, color: C.tan, letterSpacing: 2, fontWeight: 600 }}>MILL COMMAND</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          {/* stat blocks */}
          {[
            { label: 'BATCHES', value: `${activeBatches.length}`, sub: `/${totalPlanBatches + activeBatches.length}` },
            { label: 'BAGS TODAY', value: `${totalBagsToday}`, sub: '' },
            { label: 'SHIP TODAY', value: `${(calendar[todayStr] || []).length}`, sub: ' orders' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: C.tan, letterSpacing: 1, fontWeight: 600 }}>{s.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.cream }}>
                {s.value}<span style={{ fontSize: 12, fontWeight: 400, color: C.tan }}>{s.sub}</span>
              </div>
            </div>
          ))}
          {bankSummary && bankSummary.lot_count > 0 && (() => {
            const age = bankSummary.oldest_age_days ?? 0;
            const ac = agingColor(age);
            return (
              <div style={{ textAlign: 'center' }} title="Feed Bank available — oldest lot age">
                <div style={{ fontSize: 10, color: C.tan, letterSpacing: 1, fontWeight: 600 }}>
                  FEED BANK{bankSummary.buckets.stale > 0 ? ' ⚠' : ''}
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.cream }}>
                  {fmtLbs(bankSummary.total_available_lbs)}
                  <span style={{ fontSize: 12, fontWeight: 400, color: C.tan }}> lbs · </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: ac }}>{age}d</span>
                </div>
              </div>
            );
          })()}
          <span style={{ width: 1, height: 30, background: C.tan, opacity: 0.3 }} />
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.cream, fontVariantNumeric: 'tabular-nums' }}>
              {now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div style={{ fontSize: 10, color: C.tan }}>
              {now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · Kettle Falls, WA
            </div>
          </div>
        </div>
      </div>

      {/* ═══ 2. LAURA ALERTS ═══ */}
      {lauraAlerts.length > 0 && (
        <div style={{
          flexShrink: 0, background: C.white, borderRadius: 10, border: `1px solid ${C.teal}18`,
          padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, minHeight: 45,
        }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%', background: C.teal, flexShrink: 0, ...pulse,
          }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: C.teal, flexShrink: 0 }}>LAURA</span>
          <span style={{ width: 1, height: 24, background: C.border, flexShrink: 0 }} />
          <span style={{ fontSize: 16, flexShrink: 0 }}>{getAlertIcon(lauraAlerts[alertIdx % lauraAlerts.length]?.category)}</span>
          <span style={{ fontSize: 14, color: C.textDark, flex: 1 }}>
            {lauraAlerts[alertIdx % lauraAlerts.length]?.summary}
          </span>
          {lauraAlerts.length > 1 && (
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              {lauraAlerts.slice(0, 3).map((_, i) => (
                <div
                  key={i}
                  onClick={() => setAlertIdx(i)}
                  style={{
                    width: alertIdx % lauraAlerts.length === i ? 18 : 6, height: 6, borderRadius: 3,
                    background: alertIdx % lauraAlerts.length === i ? C.teal : '#d0d0d0',
                    cursor: 'pointer', transition: 'all 0.3s',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ 3. INGREDIENTS ROW ═══ */}
      <div style={{
        flexShrink: 0, background: C.white, borderRadius: 10, border: `1px solid ${C.tan}35`,
        padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10, minHeight: 40,
      }}>
        <span style={{ fontSize: 12, color: C.muted, fontWeight: 700, letterSpacing: 1, flexShrink: 0 }}>
          INGREDIENTS
        </span>
        <span style={{ width: 1, height: 24, background: C.border, flexShrink: 0 }} />
        {allClear && ingredients.length === 0 ? (
          <span style={{ fontSize: 13, color: C.sage, fontWeight: 600 }}>All ingredients stocked 8+ weeks</span>
        ) : (
          <div style={{ display: 'flex', gap: 6, flex: 1, overflowX: 'auto', alignItems: 'center' }}>
            {ingredients.map((ing, i) => {
              const statusColor = ing.status === 'order' ? '#d97706' : ing.status === 'ordered' ? C.teal : C.sage;
              const pillBg = ing.status === 'order' ? '#fef8ec' : ing.status === 'ordered' ? '#eef6f7' : 'transparent';
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                  background: pillBg, borderRadius: 6, flexShrink: 0, whiteSpace: 'nowrap',
                }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.textDark }}>{ing.name}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: statusColor }}>{ing.weeksLeft}wk</span>
                  <span style={{ fontSize: 10, color: C.muted }}>{fmtNum(ing.burnRate)}/wk</span>
                  {ing.status === 'order' && ing.reorderBy && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: '#92400e', background: '#fde68a',
                      padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase',
                    }}>ORDER BY {ing.reorderBy.toUpperCase()}</span>
                  )}
                  {ing.status === 'ordered' && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: C.teal, background: '#e0f2f4',
                      padding: '1px 6px', borderRadius: 4,
                    }}>ORDERED</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <button
          onClick={() => setShowIngModal(true)}
          style={{
            background: C.teal, color: C.white, border: 'none', borderRadius: 6,
            padding: '6px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer',
            flexShrink: 0, whiteSpace: 'nowrap', fontFamily: 'inherit',
          }}
        >📋 MONDAY ORDER</button>
      </div>

      {/* ═══ RESIZABLE SECTIONS CONTAINER ═══ */}
      <div ref={containerRef} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* ═══ 4. NOW RUNNING ═══ */}
      <div style={{
        height: `${sectionPcts[0]}%`, flexShrink: 0, background: C.white, borderRadius: 10,
        border: `1px solid ${C.tan}35`, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '4px 12px 2px', flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1.5 }}>NOW RUNNING</span>
        </div>
        <div style={{ flex: 1, display: 'flex', gap: 8, padding: '0 12px 6px', overflow: 'hidden', alignItems: 'stretch' }}>
          {activeBatches.length === 0 ? (
            <span style={{ fontSize: 13, color: C.muted, fontStyle: 'italic', alignSelf: 'center' }}>No batches running</span>
          ) : activeBatches.map(batch => {
            const st = STATION_COLORS[batch.status] || STATION_COLORS.IN_PRODUCTION;
            const elapsed = batch.started_at ? Math.round((Date.now() - new Date(batch.started_at).getTime()) / 60000) : 0;
            const hrs = Math.floor(elapsed / 60);
            const mins = elapsed % 60;
            const allocs = batch.allocations || [];
            const MAX_SHOW = 3;
            return (
              <div key={batch.id}
                onClick={() => openRecipe(batch)}
                title="Click for the mixing recipe"
                style={{
                flex: 1, background: st.bg, borderLeft: `4px solid ${st.color}`,
                borderRadius: 8, padding: '6px 12px', display: 'flex',
                flexDirection: 'column', justifyContent: 'flex-start', minWidth: 0,
                cursor: 'pointer',
              }}>
                {/* Top row: formula / batch + station status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.textDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{batch.formula_name}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{batch.batch_number} · {fmtNum(batch.planned_weight)} lbs</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: st.color, ...pulseFast }} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: st.color }}>{st.label}</span>
                    </div>
                    {batch.started_at && (
                      <div style={{ fontSize: 11, color: C.muted }}>{hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`}</div>
                    )}
                  </div>
                </div>

                {/* Who it's for: order # · farm · ship date · lbs (the focal context) */}
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${st.color}22`, display: 'flex', flexDirection: 'column', gap: 2, width: '100%', minWidth: 0 }}>
                  {allocs.length === 0 ? (
                    <span style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>Stock — no orders</span>
                  ) : (
                    <>
                      {allocs.slice(0, MAX_SHOW).map((a, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', minWidth: 0 }}>
                          <span style={{ fontWeight: 700, color: C.textDark, flexShrink: 0 }}>{a.order_number}</span>
                          <span style={{ color: C.brown, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.customer}</span>
                          <span style={{ color: C.muted, flexShrink: 0 }}>ships {fmtShipDate(a.delivery_date)}</span>
                          <span style={{ marginLeft: 'auto', fontWeight: 600, color: C.textDark, flexShrink: 0 }}>{fmtNum(a.lbs)} lbs</span>
                        </div>
                      ))}
                      {allocs.length > MAX_SHOW && (
                        <span style={{ fontSize: 12, color: C.muted }}>
                          +{allocs.length - MAX_SHOW} more order{allocs.length - MAX_SHOW !== 1 ? 's' : ''}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Drag handle 1 ── */}
      <div
        onMouseDown={handleDragStart(0)}
        style={{
          height: 7, cursor: 'row-resize', display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0, userSelect: 'none',
        }}
      >
        <div style={{ width: 40, height: 3, borderRadius: 2, background: C.border }} />
      </div>

      {/* ═══ 5. PRODUCTION PLAN ═══ */}
      <div style={{
        height: `${sectionPcts[1]}%`, flexShrink: 0, background: C.white, borderRadius: 12,
        border: `1px solid ${C.tan}35`, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          flexShrink: 0, padding: '10px 16px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.teal, ...pulse }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: C.teal }}>PRODUCTION PLAN</span>
            <span style={{ fontSize: 14, color: C.muted }}>· {totalPlanBatches} batches this week</span>
          </div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            {[
              { label: 'Bagger', color: C.sage },
              { label: 'Pellet', color: C.red },
              { label: 'Super Sack', color: C.tan },
              { label: 'Feed Bank', color: C.teal },
            ].map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
                <span style={{ fontSize: 11, color: C.muted }}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', position: 'relative' }}>
          {planDates.map(date => {
            const batches = productionPlan[date];
            const isTodayDate = isToday(date);
            const isOverdue = date < todayStr;
            const dayDate = parseDate(date);
            const dayName = DAY_NAMES[dayDate.getDay()];
            const mo = MONTH_SHORT[dayDate.getMonth()];
            const da = dayDate.getDate();
            const totalLbs = batches.reduce((s, b) => s + b.planned_weight, 0);
            const isDropTarget = dragOverDate === date;
            return (
              <div key={date}
                onDragOver={(e) => { e.preventDefault(); setDragOverDate(date); }}
                onDragLeave={() => { if (dragOverDate === date) setDragOverDate(null); }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverDate(null);
                  if (dragBatchId) { handleBatchDrop(dragBatchId, date); setDragBatchId(null); }
                }}
              >
                {/* Day header */}
                <div style={{
                  position: 'sticky', top: 0, zIndex: 2,
                  background: isOverdue ? '#fdf0f0' : isTodayDate ? '#edf6f0' : C.cream,
                  padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 12,
                  borderBottom: `1px solid ${C.border}`,
                  borderLeft: isDropTarget ? `4px solid ${C.teal}` : isOverdue ? '4px solid #dc3545' : isTodayDate ? `4px solid ${C.teal}` : '4px solid transparent',
                }}>
                  <span style={{
                    fontSize: 16, fontWeight: 700, letterSpacing: 1.5,
                    color: isOverdue ? '#dc3545' : isTodayDate ? C.sage : C.brown,
                  }}>{isOverdue ? 'OVERDUE' : dayName}</span>
                  <span style={{ fontSize: 13, color: C.muted }}>
                    {mo} {da} · {batches.length} batches · {fmtNum(Math.round(totalLbs))} lbs
                  </span>
                </div>
                {/* Batch rows */}
                {batches.map(batch => {
                  const st = STATION_COLORS[batch.status] || STATION_COLORS.PLANNED;
                  const isActive = batch.status === 'IN_PRODUCTION' || batch.status === 'PACKAGING';
                  const isDraggable = batch.status === 'PLANNED';
                  const toBag = batch.allocations.filter(a => a.destination === 'bag').reduce((s, a) => s + a.lbs, 0);
                  const toPellet = batch.allocations.filter(a => a.destination === 'pellet').reduce((s, a) => s + a.lbs, 0);
                  const toSS = batch.allocations.filter(a => a.destination === 'ss').reduce((s, a) => s + a.lbs, 0);
                  const feedBank = batch.feed_bank_excess ?? (batch.planned_weight - (batch.total_allocated ?? (toBag + toPellet + toSS)));
                  const customers = batch.allocations.map(a => {
                    return `${a.customer} (${a.package_display || `${Math.round(a.lbs / (a.package_size_lbs || 40))} bags`})`;
                  }).join(' + ');

                  return (
                    <div key={batch.id}
                      draggable={isDraggable}
                      onDragStart={() => { if (isDraggable) setDragBatchId(batch.id); }}
                      onDragEnd={() => { setDragBatchId(null); setDragOverDate(null); }}
                      style={{
                        display: 'flex', alignItems: 'center', padding: '8px 16px', gap: 12,
                        borderBottom: `1px solid ${C.border}`,
                        borderLeft: isActive ? `5px solid ${st.color}` : '5px solid transparent',
                        background: isActive ? st.bg : C.white, minHeight: 50,
                        opacity: dragBatchId === batch.id ? 0.5 : 1,
                        cursor: isDraggable ? 'grab' : 'default',
                      }}
                    >
                      {/* Feed name */}
                      <div style={{ width: 200, flexShrink: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: C.textDark }}>
                          {batch.formula_name}
                          {batch.is_morning_mill && (
                            <span title="Morning mill — super sack, before 10 AM" style={{ marginLeft: 6, fontSize: 13 }}>🌅</span>
                          )}
                          {batch.stale_warning && (
                            <span title="Batch started over 24h ago — may be stuck" style={{ marginLeft: 6, fontSize: 13 }}>⚠️</span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: C.muted }}>{batch.batch_number}</div>
                      </div>
                      {/* Total lbs */}
                      <div style={{ width: 110, textAlign: 'right', flexShrink: 0 }}>
                        <span style={{ fontSize: 16, fontWeight: 700, color: C.textDark }}>{fmtNum(Math.round(batch.planned_weight))}</span>
                        <span style={{ fontSize: 12, color: C.muted, marginLeft: 3 }}>lbs</span>
                      </div>
                      {/* Split pills */}
                      <div style={{ width: 320, display: 'flex', gap: 6, flexShrink: 0 }}>
                        {toBag > 0 && (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                            background: '#edf6f0', border: `1px solid ${C.sage}30`, borderRadius: 6,
                          }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.sage }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: C.sage }}>{fmtLbs(toBag)}</span>
                            <span style={{ fontSize: 11, color: C.sage }}>bag</span>
                          </div>
                        )}
                        {toPellet > 0 && (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                            background: '#fdf0f0', border: `1px solid ${C.red}30`, borderRadius: 6,
                          }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.red }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: C.red }}>{fmtLbs(toPellet)}</span>
                            <span style={{ fontSize: 11, color: C.red }}>pellet</span>
                          </div>
                        )}
                        {toSS > 0 && (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px',
                            background: '#f8f4ec', border: `1px solid ${C.tan}50`, borderRadius: 6,
                          }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.tan }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: C.tan }}>{fmtLbs(toSS)}</span>
                            <span style={{ fontSize: 11, color: C.tan }}>SS</span>
                          </div>
                        )}
                      </div>
                      {/* Customers */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {customers && (
                          <div style={{ fontSize: 13, color: C.textDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {customers}
                          </div>
                        )}
                      </div>
                      {/* Feed bank */}
                      <div style={{ width: 160, flexShrink: 0, textAlign: 'center' }}>
                        {feedBank > 100 && (
                          <span style={{
                            background: '#eef6f7', border: `1px solid ${C.teal}30`, borderRadius: 6,
                            padding: '4px 10px', fontSize: 13, fontWeight: 600, color: C.teal,
                          }}>+{fmtLbs(feedBank)} bank</span>
                        )}
                      </div>
                      {/* Status badge */}
                      <div style={{ width: 110, flexShrink: 0, textAlign: 'center' }}>
                        <span style={{
                          background: st.bg, color: st.color, fontWeight: 700, fontSize: 11,
                          padding: '4px 12px', borderRadius: 10, letterSpacing: 0.5,
                          textTransform: 'uppercase',
                        }}>{st.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {planDates.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>No planned batches this week</div>
          )}
          {/* Toast notification */}
          {toast && (
            <div style={{
              position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
              background: C.teal, color: '#fff', padding: '8px 20px', borderRadius: 8,
              fontSize: 14, fontWeight: 600, zIndex: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}>{toast}</div>
          )}
        </div>
      </div>

      {/* ── Drag handle 2 ── */}
      <div
        onMouseDown={handleDragStart(1)}
        style={{
          height: 7, cursor: 'row-resize', display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0, userSelect: 'none',
        }}
      >
        <div style={{ width: 40, height: 3, borderRadius: 2, background: C.border }} />
      </div>

      {/* ═══ 6. SHIPPING CALENDAR ═══ */}
      <div style={{
        flex: 1, minHeight: 0, background: C.white, borderRadius: 12,
        border: `1px solid ${C.tan}35`, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Compact header row: day labels */}
        <div style={{
          display: 'flex', flexShrink: 0, borderBottom: `1px solid ${C.border}`,
        }}>
          <div style={{
            width: 120, flexShrink: 0, padding: '6px 10px', display: 'flex',
            alignItems: 'center', gap: 6,
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, letterSpacing: 1.5 }}>SHIPPING</span>
          </div>
          {calendarColumns.map((col, idx) => {
            const isTodayCol = col.iso === todayStr;
            return (
              <React.Fragment key={col.iso}>
                {idx === 4 && <div style={{ width: 2, flexShrink: 0, background: `${C.tan}30` }} />}
                <div style={{
                  flex: 1, padding: '6px 4px', textAlign: 'center',
                  background: isTodayCol ? '#edf6f0' : 'transparent',
                  borderLeft: isTodayCol ? `3px solid ${C.teal}` : 'none',
                }}>
                  <span style={{
                    fontSize: 13, fontWeight: 700,
                    color: isTodayCol ? C.teal : C.textDark,
                  }}>
                    {SHORT_DAYS[col.date.getDay()]} {col.date.getMonth() + 1}/{col.date.getDate()}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        {/* Day columns body */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Legend sidebar */}
          <div style={{
            width: 120, flexShrink: 0, padding: '8px 8px', display: 'flex',
            flexDirection: 'column', gap: 4, borderRight: `1px solid ${C.border}`,
          }}>
            {[
              { label: 'Priority', color: C.red },
              { label: 'Store', color: C.teal },
              { label: 'Wholesale', color: C.tan },
              { label: '🚛 Milk Run', color: C.teal },
            ].map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 8, height: 3, borderRadius: 1, background: l.color }} />
                <span style={{ fontSize: 10, color: C.muted }}>{l.label}</span>
              </div>
            ))}
          </div>
          {calendarColumns.map((col, idx) => {
            const isTodayCol = col.iso === todayStr;
            const hasPriority = col.orders.some(o => o.is_rush);
            const totalPallets = col.orders.reduce((s, o) => {
              if (o.type === 'milk_run') return s + (o.totalPallets || 0);
              return s + (o.total_pallets ?? o.pallet_count ?? Math.ceil((o.total_bags ?? Math.round(o.total_lbs / 40)) / 65));
            }, 0);
            return (
              <React.Fragment key={col.iso}>
                {idx === 4 && <div style={{ width: 2, flexShrink: 0, background: `${C.tan}30` }} />}
                <div style={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  background: isTodayCol ? '#f7fbf9' : C.white,
                  borderLeft: isTodayCol ? `3px solid ${C.teal}` : `1px solid ${C.border}08`,
                  borderRight: `1px solid ${C.border}50`,
                }}>
                  {/* Order cards — scrollable */}
                  <div style={{
                    flex: 1, overflow: 'auto', padding: '3px 3px', display: 'flex',
                    flexDirection: 'column', gap: 2,
                  }}>
                    {col.orders.map(order => {
                      // ── MILK RUN CARD (compact) ──
                      if (order.type === 'milk_run') {
                        const isHwy2 = order.route === 'hwy2';
                        const routeColor = isHwy2 ? C.teal : C.tan;
                        return (
                          <div
                            key={order.id}
                            onClick={() => { setSelectedMilkRun(order); setShowMilkRunModal(true); }}
                            style={{
                              borderLeft: `3px solid ${routeColor}`, borderRadius: 4,
                              padding: '4px 6px', cursor: 'pointer', background: `${routeColor}10`,
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              minHeight: 28,
                            }}
                          >
                            <span style={{ fontSize: 12, fontWeight: 700, color: C.textDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                              🚛 {isHwy2 ? 'Hwy 2' : 'I-90'}
                            </span>
                            <span style={{ fontSize: 11, color: C.muted, flexShrink: 0, marginLeft: 4 }}>
                              {order.totalPallets}P
                            </span>
                          </div>
                        );
                      }
                      // ── REGULAR ORDER CARD (compact single line) ──
                      const pallets = order.total_pallets ?? order.pallet_count ?? 0;
                      const borderColor = order.is_rush ? C.red : (order.shipping_method || '').includes('WILL_CALL') ? C.teal : C.tan;
                      const custShort = (order.customer || '').replace(/\s+(Feed|Farm|Ranch|LLC|Inc)\b.*/i, '');
                      const delivDay = order.delivery_date ? (() => {
                        const dt = new Date(order.delivery_date + 'T12:00:00');
                        return `${SHORT_DAYS[dt.getDay()]} ${dt.getMonth() + 1}/${dt.getDate()}`;
                      })() : '';
                      // Two-stage progress fill: milled (light) with packed (darker) within it.
                      const orderedLbs = order.ordered_lbs ?? order.total_lbs;
                      const milledPct = Math.max(0, Math.min(100, order.milled_pct ?? 0));
                      const packedPct = Math.max(0, Math.min(milledPct, order.packed_pct ?? 0));
                      const progressTitle = `Milled ${fmtNum(Math.round(order.milled_lbs ?? 0))} / ${fmtNum(Math.round(orderedLbs))} (${milledPct}%) · Packed ${fmtNum(Math.round(order.packed_lbs ?? 0))} (${order.packed_pct ?? 0}%)`;
                      return (
                        <div
                          key={order.id}
                          title={progressTitle}
                          onClick={() => { setSelectedOrder(order); setShowOrderModal(true); }}
                          style={{
                            position: 'relative', overflow: 'hidden',
                            borderLeft: `3px solid ${borderColor}`, borderRadius: 4,
                            padding: '3px 6px', cursor: 'pointer', background: C.cream,
                            minHeight: 28,
                          }}
                        >
                          {/* milled layer (lighter, behind) */}
                          <div style={{
                            position: 'absolute', left: 0, top: 0, bottom: 0, width: `${milledPct}%`,
                            background: 'rgba(44,122,63,0.18)', transition: 'width 0.6s ease',
                            pointerEvents: 'none',
                          }} />
                          {/* packed layer (darker, within milled) */}
                          <div style={{
                            position: 'absolute', left: 0, top: 0, bottom: 0, width: `${packedPct}%`,
                            background: 'rgba(44,122,63,0.38)', transition: 'width 0.6s ease',
                            pointerEvents: 'none',
                          }} />
                          <div style={{
                            position: 'relative', zIndex: 1,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            fontSize: 12, fontWeight: 700, color: C.textDark, gap: 4,
                          }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                              {custShort}
                            </span>
                            <span style={{ flexShrink: 0, fontWeight: 600, fontSize: 11, color: C.muted }}>
                              {pallets}P · {fmtNum(Math.round(order.total_lbs))}#
                            </span>
                          </div>
                          {delivDay && (
                            <div style={{ position: 'relative', zIndex: 1, fontSize: 10, color: C.muted, opacity: 0.7, marginTop: 1 }}>
                              → {delivDay}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Fixed pallet footer */}
                  <div style={{
                    borderTop: `1px solid ${C.border}`, padding: '3px 4px', textAlign: 'center',
                    flexShrink: 0, background: C.white,
                  }}>
                    <span style={{
                      fontSize: 14, fontWeight: 800,
                      color: totalPallets === 0 ? '#ccc' : hasPriority ? C.red : C.textDark,
                    }}>
                      {totalPallets}
                    </span>
                    <span style={{
                      fontSize: 10, marginLeft: 2,
                      color: totalPallets === 0 ? '#ccc' : C.muted,
                    }}>pallets</span>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      </div>{/* end resizable container */}

      {/* ═══ ORDER DETAIL MODAL ═══ */}
      {showOrderModal && selectedOrder && (() => {
        const pallets = selectedOrder.pallets || [];
        const items = selectedOrder.items || [];
        const hasSS = items.some(i => i.is_supersack);
        const ssBags = items.filter(i => i.is_supersack).reduce((s, i) => s + i.number_of_packages, 0);
        const bagBags = items.filter(i => !i.is_supersack && i.bag_size > 0).reduce((s, i) => s + i.number_of_packages, 0);
        const modalPallets = selectedOrder.total_pallets ?? pallets.length;
        return (
        <div
          onClick={() => setShowOrderModal(false)}
          style={{
            position: 'fixed', inset: 0, background: '#3F202130',
            backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            background: C.white, borderRadius: 16, padding: 32, width: 700,
            maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, color: C.textDark }}>{selectedOrder.order_number}</div>
                <div style={{ fontSize: 16, color: C.muted }}>{selectedOrder.customer}</div>
              </div>
              {selectedOrder.is_rush && (
                <span style={{
                  background: C.red, color: C.white, padding: '4px 12px', borderRadius: 8,
                  fontSize: 12, fontWeight: 700,
                }}>PRIORITY</span>
              )}
            </div>

            {/* Ship & Delivery dates */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              {selectedOrder.ship_date && (
                <div style={{ flex: 1, background: '#edf6f0', border: `1px solid ${C.sage}30`, borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: C.sage, fontWeight: 600, textTransform: 'uppercase' }}>Ship Date</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.textDark }}>
                    {new Date(selectedOrder.ship_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                </div>
              )}
              {selectedOrder.delivery_date && (
                <div style={{ flex: 1, background: '#f8f4ec', border: `1px solid ${C.tan}30`, borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: C.tan, fontWeight: 600, textTransform: 'uppercase' }}>Delivery</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.textDark }}>
                    {new Date(selectedOrder.delivery_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                </div>
              )}
            </div>

            {/* Summary stats */}
            <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
              {[
                ...(hasSS ? [{ label: 'Super Sacks', value: `${ssBags}` }] : []),
                ...(bagBags > 0 ? [{ label: 'Bags', value: `${bagBags}` }] : []),
                { label: 'Weight', value: `${fmtNum(Math.round(selectedOrder.total_lbs))} lbs` },
                { label: 'Pallets', value: `${modalPallets}` },
                { label: 'Carrier', value: (selectedOrder.shipping_method || '').replace(/_/g, ' ') },
                { label: 'Status', value: selectedOrder.status.replace(/_/g, ' ') },
              ].map(s => (
                <div key={s.label}>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase' }}>{s.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.textDark }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* ── LINE ITEMS TABLE ── */}
            {items.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.textDark, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Line Items · {items.length} products
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Feed</th>
                      <th style={{ textAlign: 'center', padding: '6px 4px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Form</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Pkg</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Lbs</th>
                      <th style={{ textAlign: 'center', padding: '6px 4px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const ps = PROD_STATUS[item.production_status] || PROD_STATUS.PLANNED;
                      return (
                        <tr key={item.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: '6px 8px', fontWeight: 600, color: C.textDark }}>
                            {item.formula_name}
                            {item.is_promo && (
                              <span style={{
                                marginLeft: 6, background: C.tan, color: C.white, padding: '1px 6px',
                                borderRadius: 4, fontSize: 10, fontWeight: 700, verticalAlign: 'middle',
                              }}>PROMO</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center', padding: '6px 4px' }}>
                            <span style={{
                              display: 'inline-block', padding: '1px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                              background: item.is_pelleted ? '#fdf0f0' : '#eef6f7',
                              color: item.is_pelleted ? C.red : C.teal,
                            }}>{item.form}</span>
                          </td>
                          <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, color: C.textDark }}>
                            {item.is_supersack
                              ? `${item.number_of_packages} × SS`
                              : item.bag_size > 0
                                ? `${item.number_of_packages} × ${item.bag_size}#`
                                : '—'}
                          </td>
                          <td style={{ textAlign: 'right', padding: '6px 8px', color: C.muted }}>
                            {fmtNum(Math.round(item.total_lbs))}
                          </td>
                          <td style={{ textAlign: 'center', padding: '6px 4px' }}>
                            <span style={{
                              display: 'inline-block', padding: '1px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                              background: ps.bg, color: ps.color,
                            }}>{ps.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── PALLET PLAN ── */}
            {pallets.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.textDark, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Pallet Plan · {modalPallets} pallets
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {pallets.map((pallet, pi) => {
                    // ── SUPER SACK PALLET ──
                    if (pallet.is_supersack) {
                      const ssItem = pallet.items[0];
                      return (
                        <div key={pi} style={{
                          border: `1px solid ${C.teal}40`, borderRadius: 10, overflow: 'hidden',
                          background: '#eef6f7',
                        }}>
                          <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 12px',
                          }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: C.textDark }}>
                              {'\uD83D\uDEE2\uFE0F'} PALLET {pi + 1} — SUPER SACK
                            </span>
                            <span style={{ fontSize: 12, color: C.teal, fontWeight: 600 }}>
                              {fmtNum(pallet.total_lbs)}#
                            </span>
                          </div>
                          {ssItem && (
                            <div style={{ padding: '0 12px 8px', fontSize: 13, color: C.muted }}>
                              {ssItem.name}
                            </div>
                          )}
                        </div>
                      );
                    }

                    // ── BAG PALLET ──
                    const isFull = pallet.total_lbs >= 2600;
                    const remainLbs = 2600 - pallet.total_lbs;
                    // Items are already reversed by backend (base at bottom)
                    const visualItems = pallet.items;
                    return (
                      <div key={pi} style={{
                        border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden',
                        background: C.cream,
                      }}>
                        {/* Pallet header */}
                        <div style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '6px 12px', borderBottom: `1px solid ${C.border}`, background: C.white,
                        }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: C.textDark }}>
                            {'\uD83D\uDCE6'} PALLET {pi + 1}
                            {isFull && <span style={{ marginLeft: 6, color: C.sage, fontSize: 12 }}>&#10003;</span>}
                          </span>
                          <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>
                            {pallet.total_bags} bags · {fmtNum(pallet.total_lbs)}/{fmtNum(2600)}#
                            {!isFull && remainLbs > 0 && (
                              <span style={{ color: C.tan, marginLeft: 4 }}>({fmtNum(Math.round(remainLbs))}# space)</span>
                            )}
                          </span>
                        </div>
                        {/* Pallet items — visual stack (base at bottom) */}
                        <div style={{ padding: '6px 12px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {visualItems.map((pi2, ii) => {
                            const barPct = Math.max((pi2.lbs / 2600) * 100, 8);
                            const barColor = PALLET_BAR_COLORS[ii % PALLET_BAR_COLORS.length];
                            return (
                              <div key={ii} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{
                                  width: `${barPct}%`, minWidth: 40, height: 20, borderRadius: 4,
                                  background: barColor, opacity: 0.75,
                                }} />
                                <span style={{ fontSize: 13, color: C.textDark, whiteSpace: 'nowrap' }}>
                                  {pi2.name} <strong>&times;{pi2.bags}</strong>
                                  {pi2.is_promo && (
                                    <span style={{
                                      marginLeft: 4, background: C.tan, color: C.white, padding: '0 5px',
                                      borderRadius: 3, fontSize: 9, fontWeight: 700,
                                    }}>PROMO</span>
                                  )}
                                </span>
                                <span style={{ fontSize: 11, color: C.muted, marginLeft: 'auto' }}>
                                  {pi2.position}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              onClick={() => setShowOrderModal(false)}
              style={{
                width: '100%', padding: '10px 0', background: C.cream, border: `1px solid ${C.border}`,
                borderRadius: 8, fontSize: 14, fontWeight: 600, color: C.textDark, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >Close</button>
          </div>
        </div>
        );
      })()}

      {/* ═══ MILK RUN DETAIL MODAL ═══ */}
      {showMilkRunModal && selectedMilkRun && (() => {
        const mr = selectedMilkRun;
        const isHwy2 = mr.route === 'hwy2';
        const routeColor = isHwy2 ? C.teal : C.tan;
        const totalLbs = mr.totalLbs || mr.total_lbs || 0;
        const capLbs = mr.capacityLbs || 43000;
        const fillPct = Math.round((totalLbs / capLbs) * 100);
        const remainLbs = capLbs - totalLbs;
        const mrStops = mr.stops || [];
        const mrStatus = mr.status || 'BUILDING';
        const statusColors: Record<string, { bg: string; color: string }> = {
          BUILDING: { bg: '#f8f4ec', color: '#8a7040' },
          BOOKED: { bg: '#edf6f0', color: C.sage },
          LOADING: { bg: '#eef6f7', color: C.teal },
        };
        const sc = statusColors[mrStatus] || statusColors.BUILDING;
        return (
        <div
          onClick={() => setShowMilkRunModal(false)}
          style={{
            position: 'fixed', inset: 0, background: '#3F202130',
            backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            background: C.white, borderRadius: 16, padding: 32, width: 700,
            maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.textDark }}>
                  🚛 {mr.name || (isHwy2 ? 'Hwy 2 Milk Run' : 'I-90 Milk Run')}
                </div>
                <div style={{ fontSize: 14, color: C.muted, marginTop: 2 }}>
                  {mr.ship_date && new Date(mr.ship_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  {mr.truckerName && <span> · <strong>{mr.truckerName}</strong></span>}
                </div>
              </div>
              <span style={{
                padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                background: sc.bg, color: sc.color,
              }}>{mrStatus}</span>
            </div>

            {/* Capacity bar */}
            <div style={{
              background: C.cream, borderRadius: 10, padding: '12px 16px', marginBottom: 20,
              border: `1px solid ${C.border}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.textDark }}>
                  {fmtNum(Math.round(totalLbs))} / {fmtNum(capLbs)} lbs ({fillPct}%)
                </span>
                <span style={{ fontSize: 13, color: C.muted }}>
                  {mr.totalPallets || 0} pallets
                </span>
              </div>
              <div style={{ height: 10, borderRadius: 5, background: `${routeColor}20`, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${Math.min(fillPct, 100)}%`, borderRadius: 5,
                  background: routeColor, transition: 'width 0.3s',
                }} />
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                {fmtNum(Math.round(remainLbs))} lbs available
              </div>
            </div>

            {/* Stop list */}
            {mrStops.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.textDark, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Stops · Load Order ({mrStops.length} stops)
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {mrStops.map((stop, si) => {
                    const totalStops = mrStops.length;
                    const loadLabel = si === 0 ? 'LOAD 1st (delivers last)' :
                      si === totalStops - 1 ? `LOAD ${si + 1}${si === 1 ? 'nd' : si === 2 ? 'rd' : 'th'} (delivers first)` :
                      `LOAD ${si + 1}${si === 1 ? 'nd' : si === 2 ? 'rd' : 'th'}`;
                    const prodPct = stop.totalItems > 0 ? Math.round((stop.producedItems / stop.totalItems) * 100) : 0;
                    return (
                      <div key={si} style={{
                        border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px',
                        background: C.cream,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <div>
                            <span style={{ fontSize: 10, fontWeight: 700, color: routeColor, textTransform: 'uppercase' }}>{loadLabel}</span>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.textDark }}>{stop.customer}</div>
                            <div style={{ fontSize: 11, color: C.muted }}>{stop.city}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.textDark }}>{stop.pallets}P · {fmtNum(Math.round(stop.lbs))}#</div>
                          </div>
                        </div>
                        {/* Items */}
                        {stop.items.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6, marginBottom: 6 }}>
                            {stop.items.map((it, ii) => {
                              const ps = PROD_STATUS[it.production_status] || PROD_STATUS.PLANNED;
                              return (
                                <span key={ii} style={{
                                  fontSize: 11, padding: '2px 6px', borderRadius: 4,
                                  background: ps.bg, color: ps.color, fontWeight: 600,
                                }}>
                                  {it.feed} ×{it.bags}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {/* Production progress */}
                        {stop.totalItems > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ flex: 1, height: 4, borderRadius: 2, background: `${C.sage}20`, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${prodPct}%`, borderRadius: 2, background: C.sage }} />
                            </div>
                            <span style={{ fontSize: 10, color: C.muted, whiteSpace: 'nowrap' }}>
                              {stop.producedItems}/{stop.totalItems} produced
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quote info */}
            {mr.truckerName && (
              <div style={{
                background: C.cream, borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                border: `1px solid ${C.border}`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: 'uppercase', marginBottom: 4 }}>Booked Carrier</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.textDark }}>
                  {mr.truckerName}
                  {mr.truckerPhone && <span style={{ fontWeight: 400, color: C.muted, marginLeft: 8 }}>{mr.truckerPhone}</span>}
                </div>
                {mr.quotePrice && (
                  <div style={{ fontSize: 13, color: C.textDark, marginTop: 2 }}>
                    Rate: <strong>${fmtNum(mr.quotePrice)}</strong>
                    {(mr.totalPallets || 0) > 0 && (
                      <span style={{ color: C.muted, marginLeft: 8 }}>
                        (${Math.round(mr.quotePrice / (mr.totalPallets || 1))}/pallet)
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            {mr.notes && (
              <div style={{ fontSize: 13, color: C.muted, fontStyle: 'italic', marginBottom: 16, padding: '8px 12px', background: '#f8f4ec', borderRadius: 6 }}>
                {mr.notes}
              </div>
            )}

            <button
              onClick={() => setShowMilkRunModal(false)}
              style={{
                width: '100%', padding: '10px 0', background: C.cream, border: `1px solid ${C.border}`,
                borderRadius: 8, fontSize: 14, fontWeight: 600, color: C.textDark, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >Close</button>
          </div>
        </div>
        );
      })()}

      {/* ═══ INGREDIENT ORDER MODAL ═══ */}
      {showIngModal && (
        <div
          onClick={() => setShowIngModal(false)}
          style={{
            position: 'fixed', inset: 0, background: '#3F202130',
            backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            background: C.white, borderRadius: 16, width: 720, maxHeight: '85vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            {/* Modal header */}
            <div style={{
              background: C.brown, padding: '16px 24px', borderRadius: '16px 16px 0 0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: C.teal, ...pulse }} />
                <span style={{ fontSize: 20, fontWeight: 700, color: C.cream }}>Monday Ingredient Order</span>
                {ingredientOrder && (
                  <span style={{ fontSize: 12, color: C.tan }}>
                    · Generated {new Date(ingredientOrder.generated_at).toLocaleDateString()} · {ingredientOrder.coverage_weeks}wk coverage
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowIngModal(false)}
                style={{ background: 'transparent', border: 'none', color: C.tan, fontSize: 22, cursor: 'pointer' }}
              >×</button>
            </div>

            {/* Modal body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 0 }}>
              {!ingredientOrder ? (
                <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>
                  No ingredient order generated yet. Run the management command to generate one.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      {['INGREDIENT', 'ON HAND', '4-WK NEED', 'ORDER QTY', 'COST', 'DELIVERY'].map(h => (
                        <th key={h} style={{
                          padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                          color: C.muted, letterSpacing: 0.5, textTransform: 'uppercase',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ingredientOrder.items.map(item => {
                      const isHigh = item.urgency === 'high';
                      const isSkip = item.final_order_lbs === 0;
                      return (
                        <tr key={item.id} style={{
                          borderBottom: `1px solid ${C.border}`,
                          borderLeft: isHigh ? `4px solid #d97706` : isSkip ? `4px solid ${C.sage}` : '4px solid transparent',
                          background: isHigh ? '#fef8ec' : isSkip ? '#f7faf7' : C.white,
                        }}>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: C.textDark }}>{item.raw_material_name}</div>
                            {item.supplier && <div style={{ fontSize: 11, color: C.muted }}>{item.supplier}</div>}
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: 14, color: C.textDark }}>
                            {fmtNum(Math.round(item.current_on_hand_lbs))} lbs
                          </td>
                          <td style={{
                            padding: '10px 14px', fontSize: 14,
                            color: item.projected_need_lbs > item.current_on_hand_lbs ? C.red : C.textDark,
                            fontWeight: item.projected_need_lbs > item.current_on_hand_lbs ? 700 : 400,
                          }}>
                            {fmtNum(Math.round(item.projected_need_lbs))} lbs
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            {editingItemId === item.id ? (
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <input
                                  type="number" value={editLbs}
                                  onChange={e => setEditLbs(e.target.value)}
                                  style={{
                                    width: 80, padding: '4px 8px', border: `1px solid ${C.teal}`,
                                    borderRadius: 4, fontSize: 14, fontFamily: 'inherit',
                                  }}
                                />
                                <button onClick={() => handleEditItem(item.id)} style={{
                                  background: C.teal, color: C.white, border: 'none', borderRadius: 4,
                                  padding: '4px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                }}>OK</button>
                                <button onClick={() => setEditingItemId(null)} style={{
                                  background: 'transparent', color: C.muted, border: `1px solid ${C.border}`,
                                  borderRadius: 4, padding: '3px 6px', fontSize: 11, cursor: 'pointer',
                                }}>×</button>
                              </div>
                            ) : isSkip ? (
                              <span
                                onClick={() => { setEditingItemId(item.id); setEditLbs(''); }}
                                style={{ fontSize: 13, color: C.muted, cursor: 'pointer' }}
                              >— skip</span>
                            ) : (
                              <span
                                onClick={() => { setEditingItemId(item.id); setEditLbs(String(Math.round(item.final_order_lbs))); }}
                                style={{ fontSize: 15, fontWeight: 700, color: C.textDark, cursor: 'pointer' }}
                              >
                                {fmtNum(Math.round(item.final_order_lbs))} lbs
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: 14, color: C.textDark }}>
                            ${item.estimated_cost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            {item.estimated_delivery && (
                              <>
                                <div style={{ fontSize: 13, color: C.textDark }}>
                                  {new Date(item.estimated_delivery + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </div>
                                <div style={{ fontSize: 10, color: C.muted }}>{item.lead_time_weeks} wk lead</div>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {/* Laura notes */}
              {ingredientOrder && ingredientOrder.items.some(i => i.laura_note) && (
                <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.teal, marginBottom: 6, letterSpacing: 0.5 }}>LAURA'S NOTES</div>
                  {ingredientOrder.items.filter(i => i.laura_note).map(item => (
                    <div key={item.id} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 12 }}>
                      <span style={{ fontWeight: 700, color: C.textDark }}>{item.raw_material_name}:</span>
                      <span style={{ color: C.teal }}>{item.laura_note}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal footer */}
            {ingredientOrder && (
              <div style={{
                background: C.cream, padding: '14px 24px', borderTop: `1px solid ${C.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.textDark }}>
                    ${ingredientOrder.total_cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted }}>
                    {ingredientOrder.items.filter(i => i.final_order_lbs > 0).length} ingredients ·{' '}
                    {fmtNum(Math.round(ingredientOrder.items.reduce((s, i) => s + i.final_order_lbs, 0)))} lbs
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => setShowIngModal(false)}
                    style={{
                      background: C.white, border: `1px solid ${C.border}`, borderRadius: 8,
                      padding: '10px 20px', fontSize: 14, fontWeight: 600, color: C.textDark,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >Close</button>
                  {emailSent ? (
                    <span style={{
                      background: C.sage, color: C.white, borderRadius: 8, padding: '10px 20px',
                      fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center',
                    }}>✓ Sent to {ingredientOrder.emailed_to || emailTo}</span>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="email" placeholder="supplier@email.com" value={emailTo}
                        onChange={e => setEmailTo(e.target.value)}
                        style={{
                          padding: '8px 12px', border: `1px solid ${C.border}`, borderRadius: 8,
                          fontSize: 13, width: 200, fontFamily: 'inherit',
                        }}
                      />
                      <button
                        onClick={handleEmailOrder}
                        disabled={emailSending || !emailTo}
                        style={{
                          background: C.teal, color: C.white, border: 'none', borderRadius: 8,
                          padding: '10px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                          opacity: emailSending || !emailTo ? 0.5 : 1, fontFamily: 'inherit',
                          whiteSpace: 'nowrap',
                        }}
                      >{emailSending ? 'Sending...' : '📧 Email Order to Zion'}</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating draggable recipe window for the clicked running batch.
          Next-batch prompt: a running batch whose id differs from the one shown —
          recomputed each render (poll-safe), NEVER auto-loaded. */}
      {recipeBatch && (() => {
        const next = activeBatches.find(b => b.id !== recipeBatch.id) || null;
        return (
          <RecipeWindow
            batchNumber={recipeBatch.batch_number}
            formulaName={recipeBatch.formula_name}
            ingredients={recipeIngredients}
            loading={recipeLoading}
            onClose={() => { setRecipeBatch(null); setRecipeIngredients(null); }}
            nextLabel={next ? { formulaName: next.formula_name, batchNumber: next.batch_number } : null}
            onLoadNext={() => { if (next) openRecipe(next); }}
            savedPrefs={recipePrefs}
            onPersist={saveRecipePrefs}
          />
        );
      })()}
    </div>
  );
};

export default MillCommandDashboard;
